import "react-phone-number-input/style.css";
import React from "react";
import { Select, Tag } from "antd";
import PhoneInput from "react-phone-number-input";
import { LuCirclePlus } from "react-icons/lu";
import { CloseOutlined } from "@ant-design/icons";
import { DEFAULT_COUNTRY } from "../../config";
import CustomButton from "./button";

export const ALL_USAGE_OPTION = "All";

// Phone usage options (shared across forms)
export const PHONE_USAGE_OPTIONS = [
  { value: ALL_USAGE_OPTION, label: ALL_USAGE_OPTION },
  { value: "Nudges", label: "Nudges" },
  { value: "Audio/Video call", label: "Audio/Video call" },
  { value: "Others", label: "Others" },
];

// Just the values for display formatting (excludes "All")
export const PHONE_USAGE_VALUES = PHONE_USAGE_OPTIONS.slice(1).map(option => option.value);

// Tooltip messages for different phone usage types
export const PHONE_USAGE_TOOLTIPS = {
  Nudges: "Automated nudges will be sent to this number.",
  "Audio/Video call": "Incoming and outgoing audio/video calls will be handled using this number.",
  Others: "This number will be used for other communication purposes and general contact.",
  All: "This number will be used for all communication purposes including nudges, calls, and other notifications.",
};

const PHONE_TYPES = ["Work", "Personal", "Other"];

/**
 * Shared editable phone numbers field (Mobile + Used for + type tags + Add Number).
 *
 * Props:
 *  - phoneNumbers: array of { id, type, number, usedFor }
 *  - onUpdate(id, field, value)
 *  - onAdd()
 *  - onRemove(id)
 *  - usedForErrors: { [id]: boolean } (optional)
 *  - usageOptions: select options (defaults to PHONE_USAGE_OPTIONS)
 */
const PhoneNumbersField = ({
  phoneNumbers = [],
  onUpdate,
  onAdd,
  onRemove,
  usedForErrors = {},
  usageOptions = PHONE_USAGE_OPTIONS,
}) => {
  return (
    <div className="border-1 border-solid border-[#d9d9d9] rounded-lg p-4">
      {phoneNumbers.map((phone, index) => (
        <div key={phone.id} className="relative mb-4 last:mb-0">
          {phoneNumbers.length > 1 && index !== 0 && (
            <CloseOutlined
              className="absolute top-0 right-0 cursor-pointer text-[#bfbfbf]"
              onClick={() => onRemove(phone.id)}
            />
          )}

          <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-[red] text-sm">*</span>
                <span className="form-field-label">Mobile</span>
              </div>
              <PhoneInput
                value={phone.number}
                onChange={value => onUpdate(phone.id, "number", value)}
                international
                withCountryCallingCode
                countryCallingCodeEditable={false}
                defaultCountry={DEFAULT_COUNTRY}
                placeholder="Enter phone number"
                className="phoneInputCss w-full h-[34px]"
              />
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-[red] text-sm">*</span>
                <span className="form-field-label">Used for</span>
              </div>
              <Select
                mode="multiple"
                value={phone.usedFor}
                onChange={value => onUpdate(phone.id, "usedFor", value)}
                placeholder="Select usage options"
                className="w-full min-h-[30px]"
                maxTagCount="responsive"
                allowClear
                options={usageOptions}
                status={usedForErrors[phone.id] ? "error" : undefined}
              />
              {usedForErrors[phone.id] && (
                <span className="text-red-500 text-xs">Please select at least one option</span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1 pt-2">
            {PHONE_TYPES.map(type => (
              <Tag
                key={type}
                className={`cursor-pointer select-none ${
                  phone.type === type
                    ? "!bg-[#E6F4FF] !border-twLinkColor !text-twLinkColor"
                    : "!bg-white !border-[#d9d9d9] !text-black-88"
                }`}
                onClick={() => onUpdate(phone.id, "type", type)}
              >
                {type}
              </Tag>
            ))}
          </div>
        </div>
      ))}

      <CustomButton
        type="link"
        handleClick={onAdd}
        className="!p-0 mt-4 flex items-center gap-2 text-twLinkColor text-sm font-normal"
        icon={<LuCirclePlus className="text-twLinkColor text-base" />}
        btnText="Add Number"
      />
    </div>
  );
};

export default PhoneNumbersField;
